import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Eye, EyeOff } from 'lucide-react';
import { Button } from '@components/ui/button/Button';
import { Spinner } from '@shared/components';
import { cn } from '@shared/utils/utils';
import { useToast } from '@shared/hooks/use-toast';
import { usePermissions } from '@shared/hooks/usePermissions';
import { IntegrationProvider, IntegrationTokenResponse } from '../../models/Integrations';
import { useActivateChatby, useChatbyToken } from '../../hooks';
import { IntegrationLogo } from './IntegrationLogo';
import { ChatbyInfoPanel } from './ChatbyInfoPanel';

interface IntegrationCardProps {
  provider: IntegrationProvider;
}

const toProviderSlug = (name: string) => name.toLowerCase();

const isChatbyProvider = (name: string) => toProviderSlug(name) === 'chatby';

export function IntegrationCard({ provider }: IntegrationCardProps) {
  const { t } = useTranslation('integrationsModule');
  const { toast } = useToast();
  const { getTeamType } = usePermissions();
  const providerSlug = toProviderSlug(provider.name);
  const isTenantExternal = getTeamType() === 'tenant_external';
  const isChatby = isChatbyProvider(provider.name);
  const hasExistingToken = provider.has_token;

  const [generatedToken, setGeneratedToken] = useState<IntegrationTokenResponse | null>(null);
  const [isTokenVisible, setIsTokenVisible] = useState(false);
  const activateChatby = useActivateChatby();
  const {
    data: fetchedToken,
    isLoading: isTokenLoading,
    error,
    refetch,
  } = useChatbyToken(hasExistingToken && isChatby && isTenantExternal);

  const tokenData = generatedToken ?? activateChatby.data ?? fetchedToken ?? null;
  const isActive = tokenData?.active ?? provider.active;
  const isForbidden = (error as { statusCode?: number } | null)?.statusCode === 403;
  const tokenErrorMessage = isForbidden ? t('token.forbidden') : t('token.error');
  const showGeneratePanel = !hasExistingToken && !tokenData;
  const showTokenLoading = hasExistingToken && isTokenLoading && !tokenData;

  const handleGenerateToken = () => {
    activateChatby.mutate(undefined, {
      onSuccess: (data) => {
        setGeneratedToken(data);
      },
    });
  };

  const handleCopyToken = async () => {
    if (!tokenData?.token) return;
    await navigator.clipboard.writeText(tokenData.token);
    toast({ title: t('token.copied'), variant: 'default' });
  };

  const maskedToken = tokenData?.token.replace(/./g, '•') ?? '';

  if (!isChatby || !isTenantExternal) {
    return (
      <div
        className="rounded-lg border border-border bg-card p-5 transition-colors"
        data-testid={`integration-card-${providerSlug}`}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            {provider.logo ? (
              <IntegrationLogo
                logo={provider.logo}
                name={provider.name}
                data-testid={`integration-logo-${providerSlug}`}
              />
            ) : (
              <h2 className="text-base font-semibold text-foreground">{provider.name}</h2>
            )}
          </div>
          <StatusBadge active={provider.active} />
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-border bg-card p-5 transition-colors"
      data-testid={`integration-card-${providerSlug}`}
    >
      <div className="mb-4 flex items-start gap-2" data-testid="chatby-beta-disclaimer">
        <span className="inline-flex shrink-0 rounded bg-bg-warning px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-warning">
          {t('chatbyInfo.beta.label')}
        </span>
        <p className="text-xs leading-relaxed text-muted-foreground">{t('chatbyInfo.beta.disclaimer')}</p>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          {provider.logo ? (
            <IntegrationLogo
              logo={provider.logo}
              name={provider.name}
              data-testid={`integration-logo-${providerSlug}`}
            />
          ) : (
            <h2 className="text-base font-semibold text-foreground">{provider.name}</h2>
          )}
          <ChatbyInfoPanel />
        </div>
        <StatusBadge active={isActive} />
      </div>

      {showGeneratePanel && (
        <div className="mt-5 rounded-lg border border-border bg-muted/20 p-4">
          <h3 className="text-sm font-medium text-foreground">{t('activate.title')}</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t('activate.description')}</p>
          <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
            <li>{t('activate.steps.generate')}</li>
            <li>{t('activate.steps.copy')}</li>
            <li>{t('activate.steps.paste')}</li>
          </ol>

          <Button
            variant="primary"
            className="mt-4"
            onClick={handleGenerateToken}
            disabled={activateChatby.isPending}
            data-testid="activate-chatby-button"
          >
            {activateChatby.isPending ? <Spinner className="h-4 w-4" /> : t('activate.button')}
          </Button>
        </div>
      )}

      {showTokenLoading && (
        <div className="mt-5 flex justify-center py-6">
          <Spinner />
        </div>
      )}

      {hasExistingToken && !tokenData && error && (
        <div className="mt-5 rounded-lg border border-border bg-muted/20 p-4">
          <p className="text-sm text-muted-foreground">{tokenErrorMessage}</p>
          <Button variant="outline" className="mt-3" onClick={() => refetch()}>
            {t('token.retry')}
          </Button>
        </div>
      )}

      {tokenData && (
        <div className="mt-5 rounded-lg border border-border bg-muted/20 p-4">
          <h3 className="text-sm font-medium text-foreground">{t('token.generatedTitle')}</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t('token.generatedDescription')}</p>

          <div className="mt-4">
            <label className="text-xs font-medium text-muted-foreground">{t('token.label')}</label>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2.5">
                <p
                  className="truncate font-mono text-sm text-foreground"
                  data-testid={`integration-token-${providerSlug}`}
                >
                  {isTokenVisible ? tokenData.token : maskedToken}
                </p>
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsTokenVisible((prev) => !prev)}
                aria-label={isTokenVisible ? t('token.hide') : t('token.show')}
                className="shrink-0"
              >
                {isTokenVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>

              <Button
                variant="primary"
                onClick={handleCopyToken}
                className="shrink-0"
                data-testid="copy-chatby-token-button"
              >
                <Copy className="h-4 w-4" />
                {t('token.copy')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ active }: { active: boolean }) {
  const { t } = useTranslation('integrationsModule');

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground',
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', active ? 'bg-success' : 'bg-muted-foreground')} />
      {t(`status.${active ? 'active' : 'inactive'}`)}
    </span>
  );
}
